import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon, X } from "lucide-react"

import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import { Calendar } from "./ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover"
import { presetRanges, rangeMatches } from "../lib/dateRanges"

export function DateRangePicker({
  dateRange,
  setDateRange,
  budgetPeriod,
  className,
}) {
  const [open, setOpen] = React.useState(false)
  const presets = presetRanges(budgetPeriod)
  const activeId = presets.find((p) => rangeMatches(p.range(), dateRange))?.id
  const activePreset = presets.find((p) => p.id === activeId)
  const isCustom = (dateRange?.from || dateRange?.to) && !activeId

  let label = "Todo el tiempo"
  if (activePreset) {
    label = activePreset.label
  } else if (isCustom && dateRange?.from) {
    label = dateRange.to
      ? `${format(dateRange.from, "dd LLL")} - ${format(dateRange.to, "dd LLL")}`
      : format(dateRange.from, "dd LLL, y")
  }

  const handlePreset = (p) => {
    setDateRange(p.range())
    setOpen(false)
  }

  const handleCustomSelect = (range) => {
    setDateRange(range)
    if (range?.from && range?.to) setOpen(false)
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            data-testid="date-range-trigger"
            className={cn(
              // Matches the shared Pill style (components/ui/pill.jsx) so every
              // small rounded filter/period tag in the app looks the same —
              // kept as a Button here (not swapped for Pill) since it needs to
              // stay a real, focusable trigger for the popover.
              "hit-44 min-w-0 justify-start text-left font-normal rounded-full h-9 px-3 border bg-slate-900/50 border-slate-700 hover:bg-slate-800",
              (activePreset || isCustom) ? "border-violet-500/40 bg-violet-500/10 text-violet-300" : "text-slate-300"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800" align="end">
          <div className="flex flex-col gap-1 p-2 border-b border-slate-800">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePreset(p)}
                data-testid={`date-preset-${p.id}`}
                className={cn(
                  "text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  activeId === p.id
                    ? "bg-violet-500/15 text-violet-300"
                    : "text-slate-300 hover:bg-white/5"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleCustomSelect}
            numberOfMonths={1}
            className="bg-slate-900 text-slate-200"
          />
        </PopoverContent>
      </Popover>

      {(dateRange?.from || dateRange?.to) && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setDateRange(undefined)}
          className="hit-44 h-9 w-9 text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 shrink-0"
          title="Ver todo (sin filtro)"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
